// audienceCache — generic IDB factory for learner-side audience-based
// collections (messages, fragments, …). All these caches share the same
// shape: a doc with timestamps that needs offline-first paint while the
// live Firestore listener catches up.
//
// Usage:
//   const cache = createAudienceCache<MessageDoc>({
//     dbName: "goodbye-user-messages",
//     storeName: "messages",
//     encode: (m) => ({ ... }),  // doc → IDB row
//     decode: (s) => ({ ... }),  // IDB row → doc
//   });
//   await cache.write(messages);
//   const cached = await cache.read();
//   await cache.delete(id);

import { type IDBPDatabase, openDB } from "idb";

export interface AudienceCacheOptions<TDoc, TStored> {
  dbName: string;
  storeName: string;
  /** Serialize a runtime doc into something IDB-safe (e.g. Timestamps → ms). */
  encode: (doc: TDoc) => TStored;
  /** Hydrate an IDB row back to the runtime shape. */
  decode: (stored: TStored) => TDoc;
}

export interface AudienceCache<TDoc> {
  write(docs: TDoc[]): Promise<void>;
  read(): Promise<TDoc[]>;
  delete(id: string): Promise<void>;
}

export function createAudienceCache<
  TDoc extends { id: string },
  TStored extends { id: string },
>(opts: AudienceCacheOptions<TDoc, TStored>): AudienceCache<TDoc> {
  const { dbName, storeName, encode, decode } = opts;
  let dbPromise: Promise<IDBPDatabase> | null = null;

  function getDb(): Promise<IDBPDatabase> {
    if (!dbPromise) {
      dbPromise = openDB(dbName, 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: "id" });
          }
        },
      });
    }
    return dbPromise;
  }

  return {
    async write(docs) {
      const db = await getDb();
      const tx = db.transaction(storeName, "readwrite");
      await tx.store.clear();
      for (const d of docs) {
        await tx.store.put(encode(d));
      }
      await tx.done;
    },
    async read() {
      try {
        const db = await getDb();
        const all = (await db.getAll(storeName)) as TStored[];
        return all.map(decode);
      } catch (err) {
        console.warn(`[audienceCache:${storeName}] read failed`, err);
        return [];
      }
    },
    async delete(id) {
      try {
        const db = await getDb();
        await db.delete(storeName, id);
      } catch (err) {
        console.warn(`[audienceCache:${storeName}] delete failed`, err);
      }
    },
  };
}
